import {
    ClientManagerInstanceToken,
    ColumnModel,
    CompositeModel,
    ConfigConstants,
    CustomInjector,
    EquipmentModel,
    NotificationModel,
    TableModel,
    TableModelOptions,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';

export default class EquipmentConfigurationModel extends CompositeModel {
    public tableModel: TableModel<EquipmentModel>;
    public equipmentServiceKeyList: EquipmentModel[];
    public equipmentAllList: EquipmentModel[];
    public qmNum: string;
    public tableOptions: TableModelOptions;
    public isTruncated = false;
    public serviceKey = '';
    public process: ConfigConstants.PROCESS_INDICATOR;
    public constructor(notificationModel: NotificationModel) {
        super('EquipmentConfigurationModel');

        const injector = CustomInjector.getInjector();
        const translate = injector.get(TranslateService);
        const clientManager = injector.get(ClientManagerInstanceToken);

        const columns: ColumnModel[] = [];
        columns.push(
            new ColumnModel(
                <string>translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_DESCRIPTION'),
                'equiShortDesc.value',
            ),
        );
        // Label for the second column
        let columnLabel;

        if (clientManager.isNgmAA()) {
            columnLabel = <string>translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_FUNCTIONAL_LOCATION');
        } else if (clientManager.isPaceUS()) {
            columnLabel = <string>translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_EQUIPMENT');
        } else {
            columnLabel = <string>translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_TECHNICAL_IDENT');
        }

        // valuefield for the second column
        let columnField;
        if (clientManager.isPaceUS()) {
            columnField = 'equnr.value';
        } else {
            columnField = 'tidnr.value';
        }

        columns.push(new ColumnModel(columnLabel, columnField));
        this.tableOptions = new TableModelOptions({ columns });

        this.equipmentAllList = this.sortByNull(notificationModel.getUniqueEquipmentsByEquipmentNumber());
        this.equipmentServiceKeyList = this.sortByNull(
            this.equipmentAllList.filter((equipment) => equipment.serviceKey.value.length > 0),
        );
        this.tableModel = new TableModel<EquipmentModel>(this.equipmentAllList, this.tableOptions);
        this.qmNum = notificationModel.identifier.qmNum;
        this.process = notificationModel.processIndicator;
    }

    /**
     * Based on tab selection, this function sets the sortable list to either have
     * all the equipments or the equipments with only servicekey available
     *
     * @param index the index of the selected tab
     */
    public showModals(index: number): void {
        if (index === 0) {
            this.tableModel = new TableModel<EquipmentModel>(this.equipmentAllList, this.tableOptions);
        } else {
            this.tableModel = new TableModel<EquipmentModel>(this.equipmentServiceKeyList, this.tableOptions);
        }
        // make initial selection on change of tab
        if (this.tableModel.rows.length > 0) {
            this.tableModel.selectByIndex(0);
        }
        this.updateServiceKeyValue();
    }

    /* To sort the tidnr no in ascending order and displaying the empty tidnr no at the bottom of the list  */
    public sortByNull(sortList: EquipmentModel[]): EquipmentModel[] {
        if (!sortList) {
            return sortList;
        }
        return sortList.sort((firstEquipment, secondEquipment) => {
            if (firstEquipment.tidnr.value === secondEquipment.tidnr.value) {
                return 0;
            } else if (firstEquipment.tidnr.value === '') {
                return 1;
            } else if (secondEquipment.tidnr.value === '') {
                return -1;
            } else {
                return firstEquipment.tidnr.value > secondEquipment.tidnr.value ? 1 : -1;
            }
        });
    }

    /**
     * This function sets the service key value based on service key length
     * and decides whether to show the full service key content or partial
     */
    public updateServiceKeyValue(): void {
        // If there is no service key available, don't try to format it
        if (
            typeof this.tableModel.selectedRow === 'undefined' ||
            typeof this.tableModel.selectedRow.serviceKey.value === 'undefined'
        ) {
            this.isTruncated = false;
            this.serviceKey = '';
            return;
        }

        const serviceKeyLimits = EquipmentModel.getServiceKeyLimits();
        const sKeyLowerLimit = serviceKeyLimits.serviceKeyLowerLimit;
        const sKeyUpperLimit = serviceKeyLimits.serviceKeyUpperLimit;
        const sKeyDifferenceBetweenLimits = serviceKeyLimits.serviceKeyDifferenceBetweenLimits;
        const sKeyDifferenceOutOfLimits = serviceKeyLimits.serviceKeyDifferenceOutOfLimits;

        const serviceKey = this.tableModel.selectedRow.serviceKey.value;
        if (serviceKey.length > 0) {
            this.equipmentServiceKeyList = this.sortByNull(
                this.equipmentAllList.filter((equipment) => equipment.serviceKey.value.length > 0),
            );
        }

        if (serviceKey.length >= sKeyLowerLimit && serviceKey.length < sKeyUpperLimit) {
            this.serviceKey = serviceKey.slice(-sKeyDifferenceBetweenLimits);
            this.isTruncated = true;
            return;
        } else if (serviceKey.length >= sKeyUpperLimit) {
            this.serviceKey = serviceKey.slice(-sKeyDifferenceOutOfLimits);
            this.isTruncated = true;
            return;
        }
        this.isTruncated = false;
        this.serviceKey = serviceKey;
    }
}
